import { FetchResult } from '@apollo/client';

import {
  SteeringWalletCreateMutation,
  SteeringWalletReportingPeriod,
  SteeringWalletUpdateMutation,
  SurveyOfferCreateMutation,
  SurveyOfferDeleteMutation,
  SurveyOfferUpdateMutation,
  WalletSteeringSurveyCreateMutation,
  WalletSteeringSurveyUpdateMutation,
} from '@occc/graphql';
import {
  useSteeringWalletCreate,
  useSteeringWalletUpdate,
  useSurveyOfferCreate,
  useSurveyOfferDelete,
  useSurveyOfferUpdate,
  useWalletSteeringSurveyCreate,
  useWalletSteeringSurveyUpdate,
} from '@occc-app/dataAccessLayer/steeringWallet/mutations';
import { useChainId } from '@occc-app/Chain/hooks';

import { IncentivizeResponsesFormValues, WalletFormValues } from './types';
import { getSurveyOfferCreateOrUpdateInput } from './utils';

export const useCreateWallet = () => {
  const chainId = useChainId();
  const [createWalletMutation] = useSteeringWalletCreate();

  const createWallet = (
    values: WalletFormValues,
  ): Promise<FetchResult<SteeringWalletCreateMutation>> => {
    const { name, productGroup, countWalletBy, period } = values;

    return createWalletMutation({
      variables: {
        input: {
          chainId,
          name,
          configuration: {
            productGroupId: productGroup.id,
            // 90 days is used as a longer period will give more precise information
            reportingPeriod: SteeringWalletReportingPeriod.NinetyDays,
            basis: period,
            unitOfMeasure: countWalletBy,
          },
        },
      },
    });
  };

  return createWallet;
};

export const useUpdateWallet = () => {
  const [updateWalletMutation] = useSteeringWalletUpdate();

  const updateWallet = (
    walletId: string,
    values: WalletFormValues,
  ): Promise<FetchResult<SteeringWalletUpdateMutation>> =>
    updateWalletMutation({
      variables: {
        input: {
          uuid: walletId,
          name: values.name,
        },
      },
    });

  return updateWallet;
};

export const useUpdateSurvey = () => {
  const [updateSurveyMutation] = useWalletSteeringSurveyUpdate();

  const updateSurvey = (
    uuid: string,
    values: WalletFormValues,
  ): Promise<FetchResult<WalletSteeringSurveyUpdateMutation>> => {
    const { question, rangeMin, rangeMax } = values;

    return updateSurveyMutation({
      variables: {
        input: {
          uuid,
          questionText: question,
          minUnits: rangeMin,
          maxUnits: rangeMax,
        },
      },
    });
  };

  return updateSurvey;
};

export const useCreateSurvey = () => {
  const [createSurveyMutation] = useWalletSteeringSurveyCreate();

  const createSurvey = (
    steeringWalletUuid: string,
    values: WalletFormValues,
  ): Promise<FetchResult<WalletSteeringSurveyCreateMutation>> => {
    const { question, rangeMin, rangeMax } = values;

    return createSurveyMutation({
      variables: {
        input: {
          steeringWalletUuid,
          questionText: question,
          minUnits: rangeMin,
          maxUnits: rangeMax,
        },
      },
    });
  };

  return createSurvey;
};

type UseCreateSurveyOfferReturns = {
  createSurveyOffer: (params: {
    surveyUuid: string;
    values: IncentivizeResponsesFormValues;
  }) => Promise<FetchResult<SurveyOfferCreateMutation>>;
  loading: boolean;
};

export const useCreateSurveyOffer = (): UseCreateSurveyOfferReturns => {
  const chainId = useChainId();
  const [createSurveyOfferMutation, { loading }] = useSurveyOfferCreate({
    refetchQueries: ['surveys'],
  });

  const createSurveyOffer: UseCreateSurveyOfferReturns['createSurveyOffer'] = ({
    surveyUuid,
    values,
  }): Promise<FetchResult<SurveyOfferCreateMutation>> => {
    const createSurveyOfferInput = getSurveyOfferCreateOrUpdateInput(chainId, surveyUuid, values);

    return createSurveyOfferMutation({
      variables: {
        input: createSurveyOfferInput,
      },
    });
  };

  return { createSurveyOffer, loading };
};

type UseUpdateSurveyOfferReturns = {
  updateSurveyOffer: (params: {
    surveyUuid: string;
    values: IncentivizeResponsesFormValues;
  }) => Promise<FetchResult<SurveyOfferUpdateMutation>>;
  loading: boolean;
};

export const useUpdateSurveyOffer = (): UseUpdateSurveyOfferReturns => {
  const chainId = useChainId();
  const [updateSurveyOfferMutation, { loading }] = useSurveyOfferUpdate({
    refetchQueries: ['surveys'],
  });

  const updateSurveyOffer: UseUpdateSurveyOfferReturns['updateSurveyOffer'] = ({
    surveyUuid,
    values,
  }): Promise<FetchResult<SurveyOfferUpdateMutation>> => {
    const updateSurveyOfferInput = getSurveyOfferCreateOrUpdateInput(chainId, surveyUuid, values);

    return updateSurveyOfferMutation({
      variables: {
        input: updateSurveyOfferInput,
      },
    });
  };

  return { updateSurveyOffer, loading };
};

type UseDeleteSurveyOfferReturns = {
  deleteSurveyOffer: (surveyUuid: string) => Promise<FetchResult<SurveyOfferDeleteMutation>>;
  loading: boolean;
};

export const useDeleteSurveyOffer = (): UseDeleteSurveyOfferReturns => {
  const [deleteSurveyOfferMutation, { loading }] = useSurveyOfferDelete({
    refetchQueries: ['surveys'],
  });

  const deleteSurveyOffer: UseDeleteSurveyOfferReturns['deleteSurveyOffer'] = (
    surveyUuid: string,
  ): Promise<FetchResult<SurveyOfferDeleteMutation>> => {
    return deleteSurveyOfferMutation({
      variables: {
        surveyUuid,
      },
    });
  };

  return { deleteSurveyOffer, loading };
};
